/**
 * Pass-Statistik-Karte · Dashboard Cockpit-Widget.
 *
 * Pulls `/api/analytics/pass-stats` — strict read-only, sits next to
 * the Passing-Flow-Map. Empty system → graceful zero-state banner so
 * the card never looks broken on a freshly-wiped pod.
 */
import React, { useEffect, useState } from "react";
import { ArrowRightCircle, TrendingUp, Compass, Hash } from "lucide-react";
import { getPassStats } from "@/lib/api";

const formatM = (m) => (m == null ? "—" : `${m.toFixed(1)} m`);

export default function PassStats() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getPassStats()
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setErr(e?.message || "Failed to load"));
    return () => { cancelled = true; };
  }, []);

  if (err) {
    return (
      <div data-testid="pass-stats-card" className="bg-card border border-destructive/40 rounded-sm p-4">
        <div className="font-mono text-[11px] uppercase tracking-widest text-destructive">{err}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div data-testid="pass-stats-card" className="bg-card border border-border rounded-sm p-4">
        <div className="h-4 w-32 bg-secondary animate-pulse mb-3" />
        <div className="h-12 w-20 bg-secondary animate-pulse" />
      </div>
    );
  }

  const total = data.total_passes || 0;
  const empty = total === 0;

  return (
    <div data-testid="pass-stats-card" className="bg-card border border-border rounded-sm p-4 space-y-4">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-pink-400" />
        <h3 className="font-display font-black uppercase tracking-wider text-base">
          Pass Statistics
        </h3>
      </div>

      {empty ? (
        <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground py-4 text-center">
          No pass vectors yet — kalibriere YT-Tactics mit einem Pass-Vector, dann füllen sich die Metriken hier.
        </div>
      ) : (
        <>
          {/* Hero metric — total passes */}
          <div>
            <div className="font-display font-black text-5xl text-pink-400 tabular-nums leading-none">
              {total}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
              total pass vectors
            </div>
          </div>

          {/* Secondary metrics — avg / max length */}
          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border">
            <Metric icon={<ArrowRightCircle className="w-3 h-3" />}
                    label="avg" value={formatM(data.avg_length_m)} testid="stat-avg" />
            <Metric label="max" value={formatM(data.max_length_m)} testid="stat-max" />
            <Metric label="min" value={formatM(data.min_length_m)} testid="stat-min" />
          </div>

          {/* Length distribution — dump / mid / huck */}
          <LengthDistribution dist={data.length_distribution} total={total} />

          {/* Top tag & direction */}
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
            <Metric
              icon={<Hash className="w-3 h-3" />}
              label="top tag" value={data.top_tag || "—"}
              testid="stat-top-tag"
            />
            <Metric
              icon={<Compass className="w-3 h-3" />}
              label="most common"
              value={data.most_common_direction || "—"}
              testid="stat-direction"
            />
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ icon, label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        {icon}{label}
      </div>
      <div className="font-display font-bold text-sm text-foreground tabular-nums mt-0.5 truncate">
        {value}
      </div>
    </div>
  );
}

function LengthDistribution({ dist, total }) {
  if (!dist || total === 0) return null;
  const segments = [
    { key: "dump", label: "Dump <10m", color: "bg-emerald-400/70" },
    { key: "mid",  label: "Mid 10-25m", color: "bg-amber-400/70" },
    { key: "huck", label: "Huck ≥25m", color: "bg-pink-400/70" },
  ];
  return (
    <div className="space-y-1.5 pt-2 border-t border-border">
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
        length distribution
      </div>
      <div data-testid="length-distribution-bar" className="flex h-2 rounded-sm overflow-hidden bg-secondary">
        {segments.map((s) => {
          const n = dist[s.key] || 0;
          const pct = total > 0 ? (n / total) * 100 : 0;
          return (
            <div key={s.key} className={s.color} style={{ width: `${pct}%` }}
                 title={`${s.label}: ${n} (${pct.toFixed(0)}%)`} />
          );
        })}
      </div>
      <div className="flex justify-between font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
        {segments.map((s) => (
          <span key={s.key}>{s.label.split(" ")[0]} {dist[s.key] || 0}</span>
        ))}
      </div>
    </div>
  );
}
